import { filter } from 'rxjs/operators';
import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { IInventoryFilterRequest, IProduct, IProductCatalogsData, IProductCompany, IProductInventoryDetails, IProductOrdersKPI, IVariant, IProductHandlingUnit } from "../models/products.models";
import { APIResponse } from "src/app/core/models/api-response";
import { Observable } from "rxjs";
import { environment } from "src/environments/environment";
import { IPageCount } from "src/app/shared/models/page.model";
import { ICatalog } from '../../catalogs/models/catalogs.models';

@Injectable({
  providedIn: 'root',
})
export class ProductsService {

  constructor(private http: HttpClient) { }

    // list all  products
    public listProducts(): Observable<APIResponse<IProduct>> {
      return this.http.get<APIResponse<IProduct>>(environment.BASE_URL_API + 'products');
    }

  // products
  public listAllProducts(
    page: IPageCount,
    filter: IInventoryFilterRequest | undefined,
    search: string | undefined,
    sort_by: string = 'id',
    sort_mode: string = 'desc',
    priority: number[] = [],
  ): Observable<APIResponse<IProduct>> {
    const params = new HttpParams()
      .set('page', page.number)
      .set('limit', page.size)
      .set('filters', JSON.stringify(filter))
      .set('key', search!)
      .set('sort_by', sort_by)
      .set('sort_mode', sort_mode)
      .set('priority', JSON.stringify(priority));
    return this.http.get<APIResponse<IProduct>>(
      environment.BASE_URL_API + 'products',
      { params }
    );
  }

  public getProductById(id: number): Observable<APIResponse<IProduct>> {
    return this.http.get<APIResponse<IProduct>>(environment.BASE_URL_API + 'product/' + id);
  }

  public getProductInventoryDetails(id: number): Observable<APIResponse<IProductInventoryDetails>> {
    return this.http.get<APIResponse<IProductInventoryDetails>>(environment.BASE_URL_API + 'product/' + id + '/inventory-details');
  }

  public getProductCompanies(id: number): Observable<APIResponse<IProductCompany>> {
    return this.http.get<APIResponse<IProductCompany>>(environment.BASE_URL_API + `product/${id}/companies`);
  }

  public addProduct(product: IProduct): Observable<APIResponse<IProduct>> {
    return this.http.post<APIResponse<IProduct>>(
      environment.BASE_URL_API + 'product',
      product
    );
  }

  public updateProduct(product: IProduct, id?: number): Observable<APIResponse<IProduct>> {
    return this.http.post<APIResponse<IProduct>>(
      environment.BASE_URL_API + 'product/' + id,
      product
    );
  }

  public deleteProduct(id?: number): Observable<any> {
    return this.http.delete<APIResponse<IProduct>>(
      environment.BASE_URL_API + 'product/' + id,
    );
  }

  public deleteProducts(product_ids: number[]): Observable<APIResponse<IProduct>> {
    return this.http.delete<APIResponse<IProduct>>(
      environment.BASE_URL_API + 'products/bulk/delete',
      { body: { product_ids: product_ids } }
    );
  }

  public addProductWithVariants(product: Omit<IProduct, 'sku'>): Observable<APIResponse<IProduct>> {
    return this.http.post<APIResponse<IProduct>>(
      environment.BASE_URL_API + 'product/variant',
      product
    );
  }

  public updateVariantsProduct(id: number, product: Omit<IProduct, 'sku'>): Observable<APIResponse<IProduct>> { // here
    return this.http.post<APIResponse<IProduct>>(
      environment.BASE_URL_API + 'product/variant/' + id,
      product
    );
  }

  public improveVariants(variants: Omit<IVariant, 'values'>[]): Observable<APIResponse<IProduct>> { // here
    return this.http.post<APIResponse<IProduct>>(
      environment.BASE_URL_API + 'product/variant-update',
      {variants}
    );
  }
  public exportProducts(
    filter: IInventoryFilterRequest | undefined,
    search: string | undefined
  ){
    const params = new HttpParams()
    .set('filters', JSON.stringify(filter))
    .set('key', search!);
    return this.http.get(environment.BASE_URL_API + 'products/export', {
      responseType: 'blob', params
    });
  }

  public fetchProductsBySearch(
    page: IPageCount | undefined,
    filter: IInventoryFilterRequest | undefined, 
    keyword: string | undefined,
    priority?: number[] | undefined,
    includeDeals: boolean = true,
    minimalFields: boolean = false
  ): Observable<APIResponse<IProduct>> {
    let params = new HttpParams()
      .set('page', page?.number || 0)
      .set('limit', page?.size || 0)
      .set('filters', JSON.stringify(filter || {}))
      .set('priority', JSON.stringify(priority || []))
      .set('key', keyword || '');

      // Only add include_deals param when false (to skip slow deals fetching)
      if (!includeDeals) {
        params = params.set('include_deals', 'false');
      }

      // Add minimal_fields param when true (to skip expensive operations)
      if (minimalFields) {
        params = params.set('minimal_fields', 'true');
      }

    return this.http.get<APIResponse<IProduct>>(environment.BASE_URL_API + 'products/product-variants', {
      params
    });
  }

  public getProductCatalogs(id: number): Observable<APIResponse<IProductCatalogsData>> {
    return this.http.get<APIResponse<IProductCatalogsData>>(environment.BASE_URL_API + `product/${id}/catalogs`);
  }

  public getProductOrdersKPI(id: number): Observable<APIResponse<IProductOrdersKPI>> {
    return this.http.get<APIResponse<IProductOrdersKPI>>(environment.BASE_URL_API + `products/${id}/kpis-and-mertics`);
  }

  public setDefaultHandlingUnit(handlingUnitId: number): Observable<APIResponse<any>> {
    return this.http.post<APIResponse<any>>(
      environment.BASE_URL_API + `product/set-default-handling-unit/${handlingUnitId}`,
      {}
    );
  }

  public updateProductHandlingUnits(
    productVariantId: number,
    handlingUnits: IProductHandlingUnit[],
    mergeExisting = false
  ): Observable<APIResponse<any>> {
    return this.http.put<APIResponse<any>>(
      environment.BASE_URL_API + `product/${productVariantId}/handling-units`,
      { handling_units: handlingUnits, merge_existing: mergeExisting }
    );
  }

  /**
   * Optimized endpoint for "select all" functionality.
   * Returns minimal product data (id, product_id, name, sku, status, enabled) 
   * using a dedicated optimized backend route.
   */
  public fetchAllProductsForSelectAll(
    filter: IInventoryFilterRequest | undefined,
    keyword: string | undefined
  ): Observable<APIResponse<IProduct>> {
    let params = new HttpParams();
    
    if (filter) {
      params = params.set('filters', JSON.stringify(filter));
    }
    
    if (keyword) {
      params = params.set('key', keyword);
    }

    return this.http.get<APIResponse<IProduct>>(
      environment.BASE_URL_API + 'products/product-variants/select-all',
      { params }
    );
  }

}
